import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BlockedSignupLog } from '@/types';
import { adminAPI } from '@/services/api';
import { PAGINATION } from '@/constants';
import ConfirmDialog from '@/components/users/management/common/dialogs/ConfirmDialog';
import AlertDialog from '@/components/users/management/common/dialogs/AlertDialog';
import { BlockedListFilters } from '../admin/tabs/BlockedListTab/components/BlockedListFilters';
import { BlockedListTable } from '../admin/tabs/BlockedListTab/components/BlockedListTable';
import { useSearchFilters } from '@/hooks/common';
import { filterBlockedLogs, handleAPIError } from '@/utils';

interface BlockedListTabProps {
  logs: BlockedSignupLog[];
  loading: boolean;
  onRefresh: () => void;
  onClear?: () => void;
  customerId?: number | null; // 자신의 고객사 ID (Admin 모드) 또는 null (Superadmin 모드)
  includeDeleted?: boolean; // 차단 히스토리 포함 여부
  onIncludeDeletedChange?: (include: boolean) => void; // 차단 히스토리 체크박스 변경 핸들러
  // Superadmin 모드용
  customers?: Array<{ id: number; customer_name: string; customer_code: string }>; // 고객사 목록 (필터용)
  // UI 옵션
  isSuperAdmin?: boolean;  // 슈퍼관리자 모드 여부
  useUrlSync?: boolean;  // URL과 필터 상태 동기화 여부
}

export const BlockedListTab: React.FC<BlockedListTabProps> = ({
  logs,
  loading,
  onRefresh,
  onClear,
  customerId,
  includeDeleted: propIncludeDeleted,
  onIncludeDeletedChange,
  customers: propCustomers,
  isSuperAdmin = false,
  useUrlSync = false,
}) => {
  // 고객사 목록 설정
  const safeCustomers = isSuperAdmin ? (propCustomers || []) : [];
  
  // 필터 상태 관리
  const {
    searchQuery: [searchQuery, setSearchQuery],
    selectedCustomerFilter: [selectedCustomerFilterRaw, setSelectedCustomerFilterRaw],
    resetFilters: resetBlockedFilters,
  } = useSearchFilters('blocked', {
    searchQuery: '',
    selectedCustomerFilter: 'all',
  });
  
  // Admin 모드: URL 동기화 사용 안 함
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [localSelectedCustomerFilter, setLocalSelectedCustomerFilter] = useState('all');
  
  // 실제 사용할 필터 값
  const effectiveSearchQuery = useUrlSync ? searchQuery : localSearchQuery;
  const effectiveSelectedCustomerFilter = useUrlSync 
    ? String(selectedCustomerFilterRaw || 'all')
    : (isSuperAdmin ? localSelectedCustomerFilter : 'all');
  
  const setEffectiveSearchQuery = useUrlSync ? setSearchQuery : setLocalSearchQuery;
  const setEffectiveSelectedCustomerFilter = useUrlSync 
    ? (value: string) => setSelectedCustomerFilterRaw(value as any)
    : setLocalSelectedCustomerFilter;
  
  const [page, setPage] = useState(1);
  const [unblockingLogId, setUnblockingLogId] = useState<number | null>(null);
  const [unblockingAll, setUnblockingAll] = useState(false);
  const [confirmUnblockOpen, setConfirmUnblockOpen] = useState(false);
  const [confirmUnblockAllOpen, setConfirmUnblockAllOpen] = useState(false);
  const [logToUnblock, setLogToUnblock] = useState<number | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(propIncludeDeleted || false);
  const [unblockAllConfirmMessage, setUnblockAllConfirmMessage] = useState('');
  const pageSize = PAGINATION.USER_MANAGEMENT_PAGE_SIZE;

  // propIncludeDeleted가 변경되면 내부 상태도 업데이트
  useEffect(() => {
    if (propIncludeDeleted !== undefined) {
      setIncludeDeleted(propIncludeDeleted);
    }
  }, [propIncludeDeleted]);

  const handleIncludeDeletedChange = (checked: boolean) => {
    setIncludeDeleted(checked);
    if (onIncludeDeletedChange) {
      onIncludeDeletedChange(checked);
    }
  };

  // 검색 및 고객사 필터링 (통합 유틸리티 사용)
  const filteredLogs = useMemo(() => {
    return filterBlockedLogs(logs, {
      searchQuery: effectiveSearchQuery,
      customerFilter: effectiveSelectedCustomerFilter,
      customerId,
      isSuperAdmin,
    });
  }, [logs, effectiveSearchQuery, effectiveSelectedCustomerFilter, customerId, isSuperAdmin]);

  // 페이지네이션
  const total = filteredLogs.length;
  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const visibleLogs = filteredLogs.slice(startIndex, endIndex);

  // 개별 차단해제
  const handleUnblockClick = (logId: number) => {
    setLogToUnblock(logId);
    setConfirmUnblockOpen(true);
  };

  const handleUnblock = async () => {
    if (!logToUnblock) return;
    try {
      setUnblockingLogId(logToUnblock);
      setConfirmUnblockOpen(false);
      await adminAPI.unblockSignup(logToUnblock);
      onRefresh();
      setLogToUnblock(null);
    } catch (error: any) {
      setAlertTitle('차단 해제 실패');
      setAlertMessage(handleAPIError(error, '차단 해제에 실패했습니다.'));
      setAlertOpen(true);
    } finally {
      setUnblockingLogId(null);
    }
  };

  // 모두 차단해제 (28일 이내)
  const handleUnblockAllClick = () => {
    // 슈퍼관리자인 경우 고객사 필터 선택 필수
    if (isSuperAdmin && effectiveSelectedCustomerFilter === 'all') {
      setAlertTitle('알림');
      setAlertMessage('고객사 필터를 선택해주세요. "전체 고객사"에서는 모두 차단해제가 불가능합니다.');
      setAlertOpen(true);
      return;
    }
    
    // 확인 메시지 생성
    let confirmMessage = '';
    if (isSuperAdmin) {
      if (effectiveSelectedCustomerFilter === 'none') {
        confirmMessage = '정말로 "(미할당)" 고객사의 최근 28일 이내의 모든 차단 기록을 해제하시겠습니까?';
      } else if (effectiveSelectedCustomerFilter !== 'all') {
        const targetCustomerId = parseInt(effectiveSelectedCustomerFilter, 10);
        const customerName = safeCustomers.find(c => c.id === targetCustomerId)?.customer_name || '선택한 고객사';
        confirmMessage = `정말로 "${customerName}" 고객사의 최근 28일 이내의 모든 차단 기록을 해제하시겠습니까?`;
      }
    } else {
      // 보안: 기업 관리자는 자신의 고객사만
      confirmMessage = '정말로 최근 28일 이내의 모든 차단 기록을 해제하시겠습니까?';
    }
    
    setUnblockAllConfirmMessage(confirmMessage);
    setConfirmUnblockAllOpen(true);
  };

  const handleUnblockAll = async () => {
    try {
      setUnblockingAll(true);
      setConfirmUnblockAllOpen(false);
      
      // 슈퍼관리자인 경우 선택한 고객사 필터에 따라 해제
      if (isSuperAdmin) {
        if (effectiveSelectedCustomerFilter === 'none') {
          // "(미할당)" 선택: customerId=null로 API 호출
          await adminAPI.unblockAllRecentSignups(null, 28);
          setAlertTitle('성공');
          setAlertMessage('고객사 "(미할당)" 의 차단 기록이 모두 해제되었습니다.');
        } else if (effectiveSelectedCustomerFilter !== 'all') {
          // 특정 고객사 선택
          const targetCustomerId = parseInt(effectiveSelectedCustomerFilter, 10);
          await adminAPI.unblockAllRecentSignupsForCustomer(targetCustomerId, 28);
          const customerName = safeCustomers.find(c => c.id === targetCustomerId)?.customer_name || '선택한 고객사';
          setAlertTitle('성공');
          setAlertMessage(`${customerName}의 차단 기록이 모두 해제되었습니다.`);
        } else {
          // 이 경우는 handleUnblockAllClick에서 이미 막혔지만, 안전장치
          setAlertTitle('오류');
          setAlertMessage('고객사 필터를 선택해주세요.');
          setAlertOpen(true);
          return;
        }
      } else {
        // 일반 관리자: 자신의 고객사에 대해서만 해제
        await adminAPI.unblockAllRecentSignups(customerId || null, 28);
        setAlertTitle('성공');
        setAlertMessage('모든 차단 기록이 해제되었습니다.');
      }
      
      onRefresh();
      setAlertOpen(true);
    } catch (error: any) {
      setAlertTitle('차단 해제 실패');
      setAlertMessage(handleAPIError(error, '차단 해제에 실패했습니다.'));
      setAlertOpen(true);
    } finally {
      setUnblockingAll(false);
    }
  };

  const handleResetFilters = () => {
    if (useUrlSync) {
      setEffectiveSelectedCustomerFilter('all'); // 기본값인 "전체"로 리셋
      resetBlockedFilters();
    } else {
      setEffectiveSearchQuery('');
      if (isSuperAdmin) {
        setEffectiveSelectedCustomerFilter('all');
      }
    }
    setPage(1);
  };

  const handleCustomerFilterChange = (filter: string) => {
    if (isSuperAdmin) {
      setEffectiveSelectedCustomerFilter(filter);
      setPage(1); // 필터 변경 시 첫 페이지로
    }
    // Admin 모드에서는 변경 불가 (빈 함수)
  };

  const handleSearchChange = (query: string) => {
    setEffectiveSearchQuery(query);
    setPage(1); // 검색 시 첫 페이지로
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>차단목록 (최근 28일)</CardTitle>
          <CardDescription>
            도메인 불허/차단 등으로 가입이 거부된 요청을 보여줍니다.
          </CardDescription>
        </div>
        <div className="flex items-center space-x-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => handleIncludeDeletedChange(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700">차단 히스토리 모두 보기</span>
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleUnblockAllClick}
            disabled={loading || unblockingAll || filteredLogs.length === 0}
          >
            {unblockingAll ? '처리 중...' : '모두 차단해제'}
          </Button>
          {onClear && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onClear}
              disabled={loading || logs.length === 0}
            >
              모두 삭제
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 검색 및 필터 */}
        <BlockedListFilters
          searchQuery={effectiveSearchQuery}
          onSearchChange={handleSearchChange}
          selectedCustomerFilter={effectiveSelectedCustomerFilter}
          onCustomerFilterChange={handleCustomerFilterChange}
          customers={safeCustomers}
          isSuperAdmin={isSuperAdmin}
          loading={loading}
          onReset={handleResetFilters}
        />

        {/* 테이블 */}
        <BlockedListTable
          logs={visibleLogs}
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          isSuperAdmin={isSuperAdmin}
          unblockingLogId={unblockingLogId}
          loading={loading}
          onUnblockClick={handleUnblockClick}
          searchQuery={effectiveSearchQuery}
        />
      </CardContent>

      {/* 확인 모달 */}
      <ConfirmDialog
        open={confirmUnblockOpen}
        title="차단 해제"
        description="정말로 이 차단 기록을 해제하시겠습니까?"
        confirmText="해제"
        cancelText="취소"
        variant="default"
        onConfirm={handleUnblock}
        onCancel={() => {
          setConfirmUnblockOpen(false);
          setLogToUnblock(null);
        }}
      />

      <ConfirmDialog
        open={confirmUnblockAllOpen}
        title="모두 차단 해제"
        description={unblockAllConfirmMessage || "정말로 최근 28일 이내의 모든 차단 기록을 해제하시겠습니까?"}
        confirmText="해제"
        cancelText="취소"
        variant="default"
        onConfirm={handleUnblockAll}
        onCancel={() => setConfirmUnblockAllOpen(false)}
      />

      {/* 알림 모달 */}
      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        confirmText="확인"
        variant={alertTitle === '차단 해제 실패' ? 'destructive' : 'default'}
        onConfirm={() => setAlertOpen(false)}
      />
    </Card>
  );
};
